import { EmptyResponseSchema } from '../../../api/schemas';
import type { BillingCycle, PlanTier } from '../../../lib/plan';
import { adminApiGet, adminApiPost, adminApiPut } from '../client';
import {
  AdminBusinessListResponseSchema,
  AdminPlanChangeListResponseSchema,
  AdminPlanSetResponseSchema,
  AdminTrialSetResponseSchema,
  type AdminBusiness,
  type AdminPlanChange,
} from '../schemas';

export function listBusinesses(): Promise<{ data: AdminBusiness[] }> {
  return adminApiGet('/v1/admin/businesses', AdminBusinessListResponseSchema);
}

/** The platform kill switch: the owner can never clear this one themselves. */
export function setBusinessSuspension(
  businessId: string,
  suspended: boolean,
  idempotencyKey: string,
): Promise<undefined> {
  return adminApiPost(
    `/v1/admin/businesses/${encodeURIComponent(businessId)}/suspension`,
    EmptyResponseSchema,
    { suspended },
    idempotencyKey,
  );
}

export type SetBusinessPlanInput = {
  plan: PlanTier;
  monthlyPriceCents: number;
  /** Absent keeps the cycle on file. */
  billingCycle?: BillingCycle;
  note?: string;
  /** The admin confirms the business's Stripe subscription was already changed to match. */
  stripeUpdated?: true;
};

/** Any plan at any agreed price: the platform's lever for deals and corrections. */
export function setBusinessPlan(businessId: string, input: SetBusinessPlanInput, idempotencyKey: string) {
  return adminApiPut(
    `/v1/admin/businesses/${encodeURIComponent(businessId)}/plan`,
    AdminPlanSetResponseSchema,
    input,
    idempotencyKey,
  );
}

/** Moves the free trial to `trialEndsAt` (an ISO instant), or ends it with null. */
export function setBusinessTrial(businessId: string, trialEndsAt: string | null, idempotencyKey: string) {
  return adminApiPut(
    `/v1/admin/businesses/${encodeURIComponent(businessId)}/trial`,
    AdminTrialSetResponseSchema,
    { trialEndsAt },
    idempotencyKey,
  );
}

export async function listPlanHistory(businessId: string): Promise<AdminPlanChange[]> {
  const response = await adminApiGet(
    `/v1/admin/businesses/${encodeURIComponent(businessId)}/plan-history`,
    AdminPlanChangeListResponseSchema,
  );
  return response.data;
}
